import { LIMIT_USAGE } from 'pricing-pages-api/EarlyRequesterNames';
import { FreeToolsFAQs } from 'pricing-pages-ui-components/frequentlyAskedQuestions/FreeToolsFAQs';
import { PPUIPageHeader } from 'pricing-pages-ui-components/semantic/PageHeader';
import { PageSection } from 'pricing-pages-ui-components/semantic/PageSection';
import { useEffect } from 'react';
import { NavMarker } from 'react-rhumb';
import { CMS, MARKETING, OPERATIONS, SALES, SERVICE } from 'self-service-api/constants/ProductLines';
import { buildFilteredHighlightedProducts } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import { getPrimaryTracker } from 'usage-tracker-container';
import ProductInfoContainer from '../components/ProductInfoContainer';
import BannerSection from '../components/banners/BannerSection';
import ComparePlansLink from '../components/common/ComparePlansLink';
import ProductPageSubtitle from '../components/common/ProductPageSubtitle';
import ProductPageTitle from '../components/common/ProductPageTitle';
import { CreditsCallout } from '../components/common/credits-callout/CreditsCallout';
import CodeSplitEnvWrapper from '../components/common/env/CodeSplitEnvWrapper';
import FallbackErrorBanner from '../components/common/loading/FallbackErrorBanner';
import SuiteFeatureTable from '../components/feature-table/suite-feature-table/SuiteFeatureTable';
import HeaderLayout from '../components/layout/HeaderLayout';
import { FreePageCardContainer } from '../components/merch-card/card-containers/FreePageCardContainer';
import { FREE_TOOLS_PAGE, FREE_TOOLS_PAGE_CONTENT } from '../constants/ErrorBoundaryNames';
import { DEFAULT } from '../constants/ErrorCauses';
import { getMerchCardsToShow } from '../constants/merchCards/MerchCardsToShow';
import { NavItemEnum } from '../constants/routing/NavItems';
import { useGlobalLoading } from '../hooks/loadingHooks';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useShouldHideDataOrSuitePage } from '../hooks/useShouldHideDataOrSuitePage';
import { useQueryParamTerm, useQueryParamsEffect } from '../utils/urls/appQueryParamUtilsLegacy';
import { ErrorBoundary } from '../v2/components/error-boundary/ErrorBoundary';
import { ERROR_SEVERITY } from '../v2/components/error-boundary/ErrorSeverity';
import withErrorBoundary from '../v2/components/error-boundary/withErrorBoundary';
import { SCREENS } from '../v2/constants/tracking/Screens';
import { useIsFreePortal } from '../v2/hooks/inventory/useProductInventory';
import { trackButtonClick, trackPageView } from '../v2/utils/tracking/trackingEventUtils';
import ErrorPage from './ErrorPage';
import MissingInfoFallbackPage from './MissingInfoFallbackPage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const FreeToolsPage = () => {
  useDocumentTitle('crm');
  const isAppReady = useGlobalLoading();
  const {
    queryParamTerm
  } = useQueryParamTerm();
  useQueryParamsEffect({
    term: queryParamTerm
  });
  const {
    isFreePortal,
    loading: isInventoryLoading
  } = useIsFreePortal();
  useEffect(() => {
    getPrimaryTracker().setProperties({
      defaultedTab: '',
      tab: ''
    });
    trackPageView({
      screen: SCREENS.FREE
    });
  }, []);
  const productLevels = getMerchCardsToShow({
    isFreeToStarter: true
  });
  const marketingHighlightedProducts = buildFilteredHighlightedProducts({
    productLine: MARKETING,
    productLevels
  });
  const salesHighlightedProducts = buildFilteredHighlightedProducts({
    productLevels,
    productLine: SALES
  });
  const serviceHighlightedProducts = buildFilteredHighlightedProducts({
    productLevels,
    productLine: SERVICE
  });
  const operationsHighlightedProducts = buildFilteredHighlightedProducts({
    productLevels,
    productLine: OPERATIONS
  });
  const cmsHighlightedProducts = buildFilteredHighlightedProducts({
    productLevels,
    productLine: CMS
  });
  const productsByProductLine = {
    [MARKETING]: marketingHighlightedProducts,
    [SALES]: salesHighlightedProducts,
    [SERVICE]: serviceHighlightedProducts,
    [OPERATIONS]: operationsHighlightedProducts,
    [CMS]: cmsHighlightedProducts
    // intentionally omitting COMMERCE because commerce has no free or starter levels
  };
  const shouldHideDataOrSuitePage = useShouldHideDataOrSuitePage();
  const showFeatureTable = !shouldHideDataOrSuitePage;
  return /*#__PURE__*/_jsx(_Fragment, {
    children: /*#__PURE__*/_jsx(ErrorBoundary, {
      fallback: /*#__PURE__*/_jsx(MissingInfoFallbackPage, {
        navItem: NavItemEnum.FREE
      }),
      errorBoundaryName: FREE_TOOLS_PAGE_CONTENT,
      severity: ERROR_SEVERITY.HIGH,
      children: /*#__PURE__*/_jsxs("div", {
        "data-selenium-id": "page-success",
        children: [isAppReady && /*#__PURE__*/_jsx(NavMarker, {
          name: "FREE_TOOLS_PAGE"
        }), /*#__PURE__*/_jsx(HeaderLayout, {
          selectedNavItem: NavItemEnum.FREE,
          children: /*#__PURE__*/_jsxs(ProductInfoContainer, {
            children: [/*#__PURE__*/_jsx(BannerSection, {
              screen: NavItemEnum.FREE
            }), /*#__PURE__*/_jsx(FallbackErrorBanner, {}), /*#__PURE__*/_jsxs(PPUIPageHeader, {
              children: [/*#__PURE__*/_jsx(ProductPageTitle, {
                nonProductIdentifier: NavItemEnum.FREE
              }), /*#__PURE__*/_jsx(ProductPageSubtitle, {
                nonProductIdentifier: NavItemEnum.FREE
              })]
            }), /*#__PURE__*/_jsx(FreePageCardContainer, {}), /*#__PURE__*/_jsx(PageSection, {
              children: /*#__PURE__*/_jsx(CreditsCallout, {
                navItem: NavItemEnum.FREE
              })
            }), showFeatureTable && /*#__PURE__*/_jsx(ComparePlansLink, {}), /*#__PURE__*/_jsx(CodeSplitEnvWrapper, {
              authedComponentLoader: () => import( /* webpackChunkName: "authed-chunk" */`pricing-pages-ui-components/limit-usage/LimitUsageSection`),
              authedComponentProps: {
                isFreePortal: isFreePortal && !isInventoryLoading,
                earlyRequestName: LIMIT_USAGE,
                useCompactGrid: true,
                onExpandButtonClick: () => trackButtonClick({
                  name: 'limitInteraction',
                  action: 'clicked to expand limit cards section'
                }),
                onCollapseButtonClick: () => trackButtonClick({
                  name: 'limitInteraction',
                  action: 'clicked to collapse limit cards section'
                })
              }
            }), showFeatureTable && /*#__PURE__*/_jsx(PageSection, {
              children: /*#__PURE__*/_jsx(SuiteFeatureTable, {
                productsByProductLine: productsByProductLine,
                screen: SCREENS.FREE,
                isFreeToStarter: true
              })
            }), /*#__PURE__*/_jsx(PageSection, {
              children: /*#__PURE__*/_jsx(FreeToolsFAQs, {})
            })]
          })
        })]
      })
    })
  });
};
export default withErrorBoundary(FreeToolsPage, {
  FallbackComponent: () => /*#__PURE__*/_jsx(ErrorPage, {
    errorCause: DEFAULT
  }),
  errorBoundaryName: FREE_TOOLS_PAGE,
  severity: ERROR_SEVERITY.HIGH
});